import React from 'react';
import { useFileUrl } from '../hooks/useFileUrl';
import { isLegacyMediaRef } from '../utils/mediaStorage';

interface FileImageProps {
  src: string;
  userId?: string;
  workspaceName?: string; // For local-first file resolution
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  fallback?: React.ReactNode;
}

/**
 * Image component that resolves file:// references (and legacy media references) to actual download URLs
 * Use this instead of <img> when the src might be a file:// reference
 * Supports local-first loading when workspaceName is provided and auto-save is enabled
 */
export const FileImage: React.FC<FileImageProps> = ({ 
  src, 
  userId, 
  workspaceName,
  alt = '', 
  className = '',
  style,
  fallback
}) => {
  // Properly destructure the hook result - pass workspaceName for local-first resolution
  const { url: resolvedUrl, isLoading, error } = useFileUrl(userId, src, workspaceName);
  
  // If no src provided, show fallback
  if (!src) {
    return fallback ? <>{fallback}</> : null;
  }
  
  // Show loading state while resolving a file:// reference
  const needsResolution = src.startsWith('file://') || isLegacyMediaRef(src);
  if (isLoading || (needsResolution && !resolvedUrl && !error)) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <div className={className} style={style}>
        <div className="w-full h-full animate-pulse bg-cream-200 flex items-center justify-center">
          <div className="w-4 h-4 shrink-0 border-2 border-ink-faint border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  
  // Use resolved URL, or fall back to src for non-reference URLs
  const displayUrl = resolvedUrl || src;
  
  // Safety check: don't render if still an unresolvable reference
  if (displayUrl.startsWith('file://') || isLegacyMediaRef(displayUrl)) {
    return (
      <div className={className} style={style}>
        <div className="w-full h-full bg-cream flex items-center justify-center overflow-hidden">
          <span className="ns-t-meta max-w-full truncate px-1" title={error || 'Unavailable'}>{error || 'Unavailable'}</span>
        </div>
      </div>
    );
  }
  
  return (
    <img
      src={displayUrl}
      alt={alt}
      className={className}
      style={style}
      // A large picture is decoded off the frame that shows it, so panning it into view does not hold that frame back
      decoding="async"
      // Not dragged by itself: dropped on the canvas, the browser's own drag of a picture would leave its address as a Text node
      draggable={false}
    />
  );
};

export default FileImage;

